import { type FC, type PropsWithChildren, type ReactNode } from 'react';
import {
    Header,
    type HeaderProps,
    type HelpProps,
    Island,
    Paragraph,
    type StackGap,
    type TestProps,
} from '@direct-frontend/components';
import styles from './SettingsIslandWithTitle.module.css';

export const SETTINGS_ISLAND_DEFAULT_PROPS = {
    paddingCorrection: 'top-bottom',
    gap: '28',
} as const;

const { paddingCorrection, gap: defaultGap } = SETTINGS_ISLAND_DEFAULT_PROPS;

type SettingsIslandWithTitleProps = PropsWithChildren<
    {
        title: ReactNode;
        description?: ReactNode;
        headerAddonRight?: ReactNode;
        headerTextAddonRight?: ReactNode;
        headerCaption?: HeaderProps['caption'];
        gap?: StackGap;
        help?: (props: HelpProps) => ReactNode;
        titleHelp?: (props: HelpProps) => ReactNode;
    } & TestProps
>;

export const SettingsIslandWithTitle: FC<SettingsIslandWithTitleProps> = ({
    title,
    description,
    headerAddonRight,
    headerTextAddonRight,
    headerCaption,
    children,
    help,
    titleHelp,
    gap = defaultGap,
    'data-testid': dataTestId = 'SettingsIslandWithTitle',
    ...otherProps
}) => (
    <Island paddingCorrection={paddingCorrection} gap={gap} data-testid={dataTestId} {...otherProps}>
        {(title || description) && (
            <div>
                <Header
                    data-testid={`${dataTestId}.Header`}
                    help={titleHelp}
                    level="h2"
                    caption={headerCaption}
                    addonRight={headerAddonRight}
                    textAddonRight={headerTextAddonRight}
                >
                    {title}
                </Header>
                {description ? (
                    <Paragraph
                        data-testid={`${dataTestId}.Description`}
                        help={help}
                        role="supplementary"
                        size="m"
                        className={styles.description}
                    >
                        {description}
                    </Paragraph>
                ) : null}
            </div>
        )}
        {children}
    </Island>
);
